import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { GlowCard } from '../components/ui/GlowCard'
import { api } from '../lib/api'
import type { Proposal, PaginatedResult } from '@shared/types'
import { KanbanSkeleton } from '../components/ui/Skeletons'

const STAGES = [
  { key: 'draft', label: 'Draft', color: 'var(--text-tertiary)' },
  { key: 'sent', label: 'Sent', color: 'var(--info)' },
  { key: 'viewed', label: 'Viewed', color: 'var(--primary)' },
  { key: 'approved', label: 'Approved', color: 'var(--success)' },
  { key: 'rejected', label: 'Lost', color: 'var(--danger)' },
]

export default function Pipeline() {
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api.get<PaginatedResult<Proposal>>('/proposals?limit=100')
      setProposals(result.data)
    } catch { /* error */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  if (loading) return <KanbanSkeleton />

  const getByStage = (stage: string) =>
    proposals.filter(p => p.status === stage)

  const stageTotal = (stage: string) =>
    getByStage(stage).reduce((sum, p) => sum + (p.total ?? 0), 0)

  return (
    <div className="page-enter">
      <div className="page-header mb-6">
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Pipeline</h1>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {proposals.length} proposal{proposals.length !== 1 ? 's' : ''} &middot;
          <span className="mono" style={{ fontFamily: 'var(--font-mono)' }}>${proposals.reduce((s, p) => s + (p.total ?? 0), 0).toLocaleString()}</span> total
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 400 }}>
        {STAGES.map(stage => (
          <div key={stage.key} className="flex-shrink-0" style={{ width: 280 }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{stage.label}</span>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', background: 'var(--bg-inset)', borderRadius: 10, padding: '1px 6px' }}>
                  {getByStage(stage.key).length}
                </span>
              </div>
              <span className="mono" style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
                ${stageTotal(stage.key).toLocaleString()}
              </span>
            </div>
            <div className="space-y-2" style={{ minHeight: 100 }}>
              {getByStage(stage.key).map((p, i) => (
                <GlowCard
                  key={p.id}
                  onClick={() => navigate(`/proposals/${p.slug}`)}
                  style={{
                    padding: 12,
                    borderLeft: `3px solid ${stage.color}`,
                  }}
                  delay={100 + i * 80}
                >
                  <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>{p.title}</p>
                  <div className="flex justify-between items-center">
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                      {p.proposal_type === 'change_order' ? 'CO' : 'Proposal'}
                    </span>
                    <span className="mono" style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
                      ${(p.total ?? 0).toLocaleString()}
                    </span>
                  </div>
                  {p.expires_at && (
                    <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                      Expires {new Date(p.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </GlowCard>
              ))}
              {getByStage(stage.key).length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--text-tertiary)', border: '1px dashed var(--border)', borderRadius: 8 }}>
                  No proposals
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
